import React, { lazy } from "react"
import PropTypes from "prop-types"
import { Redirect } from "react-router-dom"

const RedirectActionPlanFolder = ({ match }) => (
  <Redirect to={`/setting/action-plans/folder/${match.params.id}`} />
)
RedirectActionPlanFolder.displayName = "RedirectActionPlanFolder"
RedirectActionPlanFolder.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
}

const RedirectActionPlans = () => <Redirect to="/setting/action-plans" />
RedirectActionPlans.displayName = "RedirectActionPlans"

// Eager-loaded auth pages (kept small for fast /login)
import Login from "../pages/Authentication/Login"
import ResetPasswords from "../pages/Authentication/ResetPassword"
import Logout from "../pages/Authentication/Logout"
import ForgetPassword from "../pages/Authentication/ForgetPassword"

// Lazy-loaded pages (split into separate chunks)
const WorkSheetFront = lazy(() => import("../pages/Clients/WorkSheet-form"))
const DashboardNew = lazy(() => import("../pages/Dashboard/index"))
const OurResource = lazy(() => import("../pages/Office/Resource"))
const Events = lazy(() => import("../pages/Office/Events"))
const Messages = lazy(() => import("../pages/Office/message"))
const Calendar = lazy(() => import("../pages/Calendar"))
const TransactionDashboard = lazy(() => import("../pages/Transaction/index"))
const ReportGoals = lazy(() => import("../pages/Transaction/goal"))
const UploadFile = lazy(() => import("../pages/Lead/eligibility/upload-file"))
const Project = lazy(() => import("../pages/Pre-Construction/Projects"))
const ProjectCompare = lazy(() => import("../pages/Pre-Construction/Projects/compare"))
const ProjectsCreate = lazy(() => import("../pages/Pre-Construction/Projects/projects-create"))
const ProjectUpdate = lazy(() => import("../pages/Pre-Construction/Projects/projects-update"))
const ProjectsFeature = lazy(() => import("../pages/Pre-Construction/Projects/projects-feature"))
const ProjectsContact = lazy(() => import("../pages/Pre-Construction/Projects/projects-contact"))
const ProjectsMedia = lazy(() => import("../pages/Pre-Construction/Projects/projects-media"))
const ProjectsGallery = lazy(() => import("../pages/Pre-Construction/Projects/projects-gallery"))
const ProjectLink = lazy(() => import("../pages/Pre-Construction/Projects/projects-link"))
const ProjectsMarketing = lazy(() => import("../pages/Pre-Construction/Projects/projects-marketing"))
const ProjectDetail = lazy(() => import("../pages/Pre-Construction/Projects/project-detail"))
const Campaign = lazy(() => import("../pages/Pre-Construction/Projects/Campaign"))
const ProjectIntegration = lazy(() => import("../pages/Pre-Construction/Projects/Project-integration"))
const ProjectLandingPage = lazy(() => import("../pages/Pre-Construction/Projects/Project-landing-page"))
const ProjectOpenHousePage = lazy(() => import("../pages/Pre-Construction/Projects/Project-open-house-page"))
const ProjectOpenHouseForm = lazy(() => import("../pages/Pre-Construction/Projects/Project-open-house-form"))
const ProjectDownloadPricePage = lazy(() => import("../pages/Pre-Construction/Projects/Project-download-price-page"))
const ProjectDownloadPriceForm = lazy(() => import("../pages/Pre-Construction/Projects/Project-download-price-form"))
const ProjectWorksheetForm = lazy(() => import("../pages/Pre-Construction/Projects/Project-worksheet-form"))
const ProjectWorksheetThank = lazy(() => import("../pages/Pre-Construction/Projects/Project-worksheet-thank"))
const projectCurrent = lazy(() => import("../pages/Pre-Construction/Projects/Current-Prospects"))
const projectSrr = lazy(() => import("../pages/Pre-Construction/Projects/project-srr"))
const projectClient = lazy(() => import("../pages/Pre-Construction/Projects/project-client"))
const projectDownloadPrice = lazy(() => import("../pages/Pre-Construction/Projects/project-download-price"))
const projectMail = lazy(() => import("../pages/Pre-Construction/Projects/project-mail"))
const projectMarket = lazy(() =>
  import(/* webpackPrefetch: true */ "../pages/Pre-Construction/Projects/project-market")
)
const projectSocialMedia = lazy(() =>
  import(/* webpackPrefetch: true */ "../pages/Pre-Construction/Projects/project-social-media")
)
const srrCreate = lazy(() => import("../pages/Pre-Construction/Projects/project-srr-create"))
const landingPage = lazy(() => import("../pages/Pre-Construction/Projects/landingPage"))
const MasterLandingEdit = lazy(() => import("../pages/Master/master-landing-edit"))
const projectWorksheet = lazy(() => import("../pages/Pre-Construction/Projects/project-worksheet"))
const projectRsvp = lazy(() => import("../pages/Pre-Construction/Projects/project-rsvp"))
const massMailDashboard = lazy(() => import("../pages/Pre-Construction/Projects/project-mail-dashboard"))
const emaillandingPage = lazy(() => import("../pages/Pre-Construction/Projects/project-email-landingpage"))
const projectSelectTemp = lazy(() => import("../pages/Pre-Construction/Projects/project-select-template-mail"))
const projectMailEditor = lazy(() => import("../pages/Pre-Construction/Projects/project-mail-editor"))
const projectMailNew = lazy(() => import("../pages/Pre-Construction/Projects/project-mail-new"))
const projectMailSubmit = lazy(() => import("../pages/Pre-Construction/Projects/project-mail-submit"))
const projectLandingEditor = lazy(() => import("../pages/Pre-Construction/Projects/landing-mail-editor"))
const projectLandingSubmit = lazy(() => import("../pages/Pre-Construction/Projects/landing-page-submit"))
const projectsSetting = lazy(() => import("../pages/Pre-Construction/Projects/projects-setting"))
const ProjectLead = lazy(() => import("../pages/Pre-Construction/Lead/index"))
const PendingWorksheetLead = lazy(() => import("../pages/Pre-Construction/Lead/pending-worksheet"))
const SubmittedWorksheetLead = lazy(() => import("../pages/Pre-Construction/Lead/submitted-worksheet"))
const ProjectClients = lazy(() => import("../pages/Pre-Construction/Clients/index"))
const ProjectsEdit = lazy(() => import("../pages/Pre-Construction/Projects/editproject/projects-edit"))
const ProjectEditFeature = lazy(() => import("../pages/Pre-Construction/Projects/editproject/projects-feature-edit"))
const ProjectEditContact = lazy(() => import("../pages/Pre-Construction/Projects/editproject/projects-contact-edit"))
const ProjectEditMedia = lazy(() => import("../pages/Pre-Construction/Projects/editproject/projects-media-edit"))
const ProjectsEditGallery = lazy(() => import("../pages/Pre-Construction/Projects/editproject/projects-gallery-edit"))
const ProjectEditMarketing = lazy(() => import("../pages/Pre-Construction/Projects/editproject/projects-marketing-edit"))
const Builder = lazy(() => import("../pages/Pre-Construction/Builder"))
const BuilderDetail = lazy(() => import("../pages/Pre-Construction/Builder/Builder-detail"))
const BuilderCreate = lazy(() => import("../pages/Pre-Construction/Builder/Builder-create"))
const ProjectBuilderDetail = lazy(() => import("../pages/Pre-Construction/Builder/Project-detail"))
const Dashboard = lazy(() => import("../pages/Lead/index"))
const LeadInsight = lazy(() => import("../pages/Lead/lead-insight"))
const LeadList = lazy(() => import("../pages/Lead/Leadlist"))
const LeadDetail = lazy(() => import("../pages/Lead/Lead-detail"))
const BuyerQuestionAnswer = lazy(() => import("../pages/Lead/questions/buyer"))
const SellerQuestionAnswer = lazy(() => import("../pages/Lead/questions/seller"))
const InvestorQuestionAnswer = lazy(() => import("../pages/Lead/questions/investor"))
const BuyerEligibility = lazy(() => import("../pages/Lead/questions/buyer-eligibility"))
const CreateLead = lazy(() => import("../pages/Lead/forms/lead-create"))
const AcquireLead = lazy(() => import("../pages/Lead/acquire-lead"))
const importLead = lazy(() => import("../pages/Lead/import/import-lead"))
const mapImportLead = lazy(() => import("../pages/Lead/import/map-import-lead"))
const FinalImportLead = lazy(() => import("../pages/Lead/import/final-import-lead"))
const webLeadForm = lazy(() => import("../pages/Lead/web-lead-form"))
const newWebLeadForm = lazy(() => import("../pages/Lead/new-web-lead-form"))
const newWebLeadFormDesign = lazy(() => import("../pages/Lead/new-web-lead-form-design"))
const LeadStagePage = lazy(() => import("../pages/Lead/filter-menu/lead-stage"))
const leadTimeline = lazy(() => import("../pages/Lead/filter-menu/lead-timeline"))
const leadPipeStatus = lazy(() => import("../pages/Lead/lead-pipe-status"))
const LeadEngageMentView = lazy(() => import("../pages/Lead/filter-menu/lead-engagement-view"))
const LeadStatusReport = lazy(() => import("../pages/Lead/filter-menu/lead-report-status"))
const LeadTypeReport = lazy(() => import("../pages/Lead/filter-menu/lead-report-type"))
const LeadGroupReport = lazy(() => import("../pages/Lead/filter-menu/lead-report-group"))
const LeadEligibility = lazy(() => import("../pages/Lead/eligibility/index"))
const DecisionEligibility = lazy(() => import("../pages/Lead/eligibility/decision"))
const MortgageEligibility = lazy(() => import("../pages/Lead/eligibility/mortgage"))
const ReasonEligibility = lazy(() => import("../pages/Lead/eligibility/reason"))
const Activity = lazy(() => import("../pages/Activities"))
const Cal = lazy(() => import("../pages/Activities/cal"))
const Appointment = lazy(() => import("../pages/Activities/Appointment"))
const Notes = lazy(() => import("../pages/Activities/Notes"))
const ourTask = lazy(() => import("../pages/Task/index"))
const TasksCreate = lazy(() => import("../pages/Task/create-task"))
const ActionPlan = lazy(() => import("../pages/ActionPlan/index"))
const ActionPlanCreate = lazy(() => import("../pages/ActionPlan/action-plan-create"))
const ActionPlanByFolder = lazy(() => import("../pages/ActionPlan/action-plan-by-folder"))
const Contacts = lazy(() => import("../pages/Contacts"))
const ImportContact = lazy(() => import("../pages/Contacts/import/import-lead"))
const MapImportContact = lazy(() => import("../pages/Contacts/import/map-import-lead"))
const FinalImportContact = lazy(() => import("../pages/Contacts/import/final-import-lead"))
const ContactDetail = lazy(() => import("../pages/Contacts/Contact-detail"))
const ContactBuyerQuestionAnswer = lazy(() => import("../pages/Contacts/questions/buyer"))
const ContactAdditionProfile = lazy(() => import("../pages/Contacts/questions/profile"))
const ContactSellerQuestionAnswer = lazy(() => import("../pages/Contacts/questions/seller"))
const ConatctInvestorQuestionAnswer = lazy(() => import("../pages/Contacts/questions/investor"))
const ContactReport = lazy(() => import("../pages/Contacts/Contact-reporting"))
const ContactCreate = lazy(() => import("../pages/Contacts/contact-create"))
const AllContacts = lazy(() => import("../pages/Contacts/All"))
const EditContact = lazy(() => import("../pages/Contacts/contact-edit"))
const ClassificationContact = lazy(() => import("../pages/Contacts/filter-menu/classification"))
const ClientActivity = lazy(() => import("../pages/Contacts/filter-menu/connect-frequency"))
const ContactReview = lazy(() => import("../pages/Contacts/Contact-review"))
const ContactAllReferral = lazy(() => import("../pages/Contacts/filter-menu/all-referral"))
const ReviewHistory = lazy(() => import("../pages/Contacts/review-history"))
const EmailHistory = lazy(() => import("../pages/Contacts/review-email-history"))
const EmailComposer = lazy(() => import("../pages/Email"))
const EmailSec = lazy(() => import("../pages/Email/email-sec"))
const NewEmail = lazy(() => import("../pages/Email/newemail"))
const Clients = lazy(() => import("../pages/Clients"))
const InClients = lazy(() => import("../pages/Clients/in-active"))
const ImportClient = lazy(() => import("../pages/Clients/import/import-client"))
const MapImportClient = lazy(() => import("../pages/Clients/import/map-import-lead"))
const FinalImportClient = lazy(() => import("../pages/Clients/import/final-import-lead"))
const ClientCreate = lazy(() => import("../pages/Clients/client-create"))
const ClientDashbord = lazy(() => import("../pages/Clients/client-dashboard"))
const ClientProfile = lazy(() => import("../pages/Clients/client-profile"))
const clientSecProfile = lazy(() => import("../pages/Clients/client-sec-profile"))
const clientImpProfile = lazy(() => import("../pages/Clients/client-imp-profile"))
const clientFaimilyProfile = lazy(() => import("../pages/Clients/client-faimily-profile"))
const ClientMessage = lazy(() => import("../pages/Clients/client-message"))
const ClientDocument = lazy(() => import("../pages/Clients/client-documents"))
const ClientApp = lazy(() => import("../pages/Clients/client-app"))
const ClientDeal = lazy(() => import("../pages/Clients/client-deal"))
const ClientEngagement = lazy(() => import("../pages/Clients/engagement"))
const ClientNote = lazy(() => import("../pages/Clients/client-note"))
const ClientActionPlans = lazy(() => import("../pages/Clients/client-action-plans"))
const ClientSale = lazy(() => import("../pages/Clients/client-sale"))
const ClientSellerPropertyPage = lazy(() => import("../pages/Clients/client-seller-property-page"))
const ClientSalePref = lazy(() => import("../pages/Clients/client-sale-pref"))
const ClientSellerLeads = lazy(() => import("../pages/Clients/client-seller-leads"))
const ClientSellerShow = lazy(() => import("../pages/Clients/client-seller-showings"))
const ClientSellerClose = lazy(() => import("../pages/Clients/client-seller-closing"))
const ClientSellerOpen = lazy(() => import("../pages/Clients/client-seller-open"))
const ClientSellerCheck = lazy(() => import("../pages/Clients/client-seller-checklist"))
const ClientSellerMarket = lazy(() => import("../pages/Clients/client-seller-market"))
const OpenList = lazy(() => import("../pages/Clients/open-list"))
const ClientLeadDetail = lazy(() => import("../pages/Clients/client-open-lead"))
const ClientBuyPref = lazy(() => import("../pages/Clients/client-buyer-pref"))
const ClientBuyerClose = lazy(() => import("../pages/Clients/client-buyer-closing"))
const ClientBuyerMarket = lazy(() => import("../pages/Clients/client-buyer-market"))
const ClientBuyerCheck = lazy(() => import("../pages/Clients/client-buyer-checklist"))
const ClientLeadForm = lazy(() => import("../pages/Clients/Client-lead-form"))
const ClientInvPref = lazy(() => import("../pages/Clients/client-inv-pref"))
const ClientInvProj = lazy(() => import("../pages/Clients/client-inv-proj"))
const ClientHomePref = lazy(() => import("../pages/Clients/client-home-pref"))
const clientListing = lazy(() => import("../pages/Clients/client-listing"))
const Test = lazy(() => import("../pages/Contacts/test"))
const ThemeOne = lazy(() => import("../pages/Pre-Construction/Themes"))
const ReferalSend = lazy(() => import("../pages/Contacts/ReferalSend"))
const ReferalTemplate = lazy(() => import("../pages/Contacts/ReferalTemplate"))
const ReviewSend = lazy(() => import("../pages/Contacts/ReviewSend"))
const ReviewTemplate = lazy(() => import("../pages/Contacts/ReviewTemplate"))
const agentProfile = lazy(() => import("../pages/Settings/agent-profile"))
const GenralCurrency = lazy(() => import("../pages/Settings/general/currency"))
const EmailSignature = lazy(() => import("../pages/Settings/general/signature"))
const EmailMapper = lazy(() => import("../pages/Settings/general/emailmapper"))
const SocialMedia = lazy(() => import("../pages/Settings/general/social-media"))
const GenralSecurity = lazy(() => import("../pages/Settings/general/security"))
const GenralCalendar = lazy(() => import("../pages/Settings/general/calender"))
const ConnectedAppsPage = lazy(() => import("../pages/Settings/general/connected-apps"))
const memberInfo = lazy(() => import("../pages/Settings/member-info"))
const CommunicationPage = lazy(() => import("../pages/Settings/communication"))
const CommunicationMessagingSetup = lazy(() =>
  import("../pages/Settings/communication-messaging-setup")
)
const techSupport = lazy(() => import("../pages/Settings/tech-support"))
const settingObject = lazy(() => import("../pages/Settings/setting-object"))
const manageMarketing = lazy(() => import("../pages/Settings/manage-marketing"))
const reviewDistribution = lazy(() => import("../pages/Settings/review-distribution"))
const AppointmentLocationTypes = lazy(() => import("../pages/Settings/AppointmentLocationTypes"))
const AppointmentPriority = lazy(() => import("../pages/Settings/AppointmentPriority"))
const AppointmentType = lazy(() => import("../pages/Settings/AppointmentType"))
const ContactLevel = lazy(() => import("../pages/Settings/ContactLevel"))
const ContactStatus = lazy(() => import("../pages/Settings/ContactStatus"))
const ContactType = lazy(() => import("../pages/Settings/ContactType"))
const LeadClassifications = lazy(() => import("../pages/Settings/LeadClassifications"))
const LeadSource = lazy(() => import("../pages/Settings/LeadSources"))
const LeadStatus = lazy(() => import("../pages/Settings/LeadStatuses"))
const LeadType = lazy(() => import("../pages/Settings/LeadTypes"))
const LeadStage = lazy(() => import("../pages/Settings/LeadStage"))
const NoteType = lazy(() => import("../pages/Settings/NoteTypes"))
const ProjectDocumentType = lazy(() => import("../pages/Settings/ProjectDocumentTypes"))
const ProjectStatus = lazy(() => import("../pages/Settings/ProjectStatuses"))
const ProjectType = lazy(() => import("../pages/Settings/ProjectTypes"))
const ServiceType = lazy(() => import("../pages/Settings/ServiceTypes"))
const TaskPriority = lazy(() => import("../pages/Settings/TaskPriorities"))
const TaskReminder = lazy(() => import("../pages/Settings/TaskReminders"))
const TaskStatus = lazy(() => import("../pages/Settings/TaskStatuses"))
const TaskType = lazy(() => import("../pages/Settings/TaskType"))
const Users = lazy(() => import("../pages/Settings/users"))
const AddUser = lazy(() => import("../pages/Settings/create-users"))
const UserPermission = lazy(() => import("../pages/Settings/permission"))
const TransactionType = lazy(() => import("../pages/Settings/TransactionType"))
const TransactionCommType = lazy(() => import("../pages/Settings/TransactionCommType"))
const TransactionRefType = lazy(() => import("../pages/Settings/TransactionRefType"))
const TransactionStatusType = lazy(() => import("../pages/Settings/TransactionStatusType"))
const ListingType = lazy(() => import("../pages/Settings/ListingTypes"))
const ListingStatus = lazy(() => import("../pages/Settings/ListingStatus"))
const ShowingStatus = lazy(() => import("../pages/Settings/ShowingStatus"))
const EmailListType = lazy(() => import("../pages/Settings/EmailListTypes"))
const EmailListTarget = lazy(() => import("../pages/Settings/EmailListTarget"))
const CampaignEmailList = lazy(() => import("../pages/Settings/CampaignEmailList"))
const ourTemplates = lazy(() => import("../pages/Templates/index"))
const ReferalTemp = lazy(() => import("../pages/Templates/referal-template"))
const ReviewTemp = lazy(() => import("../pages/Templates/review-template"))
const formDesign = lazy(() => import("../pages/Templates/form-design"))
const CreateComp = lazy(() => import("../pages/Campaign/index"))
const CreateCompTemp = lazy(() => import("../pages/Campaign/create"))
const CreateCompAutoTemp = lazy(() => import("../pages/Campaign/create-autoload"))
const chooseTemplateComp = lazy(() => import("../pages/Campaign/choose"))
const formDesignEdit = lazy(() => import("../pages/Campaign/form-design-edit"))
const compDetail = lazy(() => import("../pages/Campaign/comp-detail"))
const ReviewCamp = lazy(() => import("../pages/Campaign/review"))
const SurveyCamp = lazy(() => import("../pages/Campaign/survey"))
const ReffralCamp = lazy(() => import("../pages/Campaign/referral"))
const NewsletterCamp = lazy(() => import("../pages/Campaign/newsletter"))
const SellerCamp = lazy(() => import("../pages/Campaign/seller"))
const campaignEmailPage = lazy(() => import("../pages/Campaign/campaign-email-page"))
const SocialPage = lazy(() => import("../pages/Social/index"))
const Calender = lazy(() => import("../pages/Calendar/index"))
const CollectionDashboard = lazy(() => import("../pages/Collections/index"))
const CollectionList = lazy(() => import("../pages/Collections/collectionList"))
const CustomTemplates = lazy(() => import("../pages/Editor/custome-template"))
const UnlayerEditor = lazy(() => import("../pages/Editor/craft-create-email-template"))
const Sendcamp = lazy(() => import("../pages/Campaign/send-camp"))
const ProjectInviteUser = lazy(() => import("../pages/Pre-Construction/Projects/Project-Invite-User"))
const projectInviteEditor = lazy(() => import("../pages/Pre-Construction/Rsvp/project-invite-editor"))
const SendInvite = lazy(() => import("../pages/Pre-Construction/Rsvp/send-invite"))
const MasterEmail = lazy(() => import("../pages/Editor/master-email"))
const PreviewLandingPage = lazy(() => import("../pages/Pre-Construction/landing-page/preview-landing-page"))
const editorLandingpage = lazy(() => import("../pages/Pre-Construction/landing-page/editor-landingpage"))
const landingPageCreateNew = lazy(() => import("../pages/Pre-Construction/landing-page/create-new"))
const MasterLandingPageList = lazy(() => import("../pages/Master/master-landing-page"))
const MasterLandingCreate = lazy(() => import("../pages/Master/master-landing-create"))
const PublishPage = lazy(() => import("../pages/Pre-Construction/landing-page/publish-page"))
const finalImportLead = lazy(() => import("../pages/Lead/import/final-import-lead"))
const EmailInbox = lazy(() => import("../pages/Email/email-inbox"))
const EmailRead = lazy(() => import("../pages/Email/email-read"))
const EmailSent = lazy(() => import("../pages/Email/email-sent"))
const EmailAssign = lazy(() => import("../pages/Email/email-assign"))
const GmailLanding = lazy(() => import("../pages/Email/gmail"))
const GoogleLanding = lazy(() => import("../pages/Calendar/google"))
const rentalTimeline = lazy(() => import("../pages/Clients/rental-timeline"))
const previewPage = lazy(() => import("../pages/preview-page"))
const PreviewNewPage = lazy(() => import("../pages/preview-page/preview"))
const projectMailEditorNew = lazy(() => import("../pages/Pre-Construction/Projects/project-mail-editor-new"))
const projectSrrPrint = lazy(() => import("../pages/Pre-Construction/Projects/project-srr-print"))
const DealDashboard = lazy(() => import("../pages/Transaction/deal-dasbhoard"))
const projectBudget = lazy(() =>
  import(/* webpackPrefetch: true */ "../pages/Pre-Construction/Projects/project-budget")
)
const projectDraftMailEditor = lazy(() => import("../pages/Pre-Construction/Projects/project-draft-mail-editor"))
const ProjectWorksheetInquiry = lazy(() => import("../pages/Pre-Construction/Projects/Project-worksheet-inquiry"))
const eligibilityOverview = lazy(() => import("../pages/Lead/eligibility/eligibility-overview"))
const leadGeneration = lazy(() => import("../pages/Lead/lead-generation"))
const leadEvents = lazy(() => import("../pages/Lead/lead-events"))
const leadReferrals = lazy(() => import("../pages/Lead/lead-referrals"))
const leadReferralsLanding = lazy(() => import("../pages/Lead/referrals-landing"))
const leadReferralsLandingEditor = lazy(() => import("../pages/Lead/referrals-landing-editor"))
const leadHomeValuation = lazy(() => import("../pages/Lead/home-valuation"))
const leadAssignmentSales = lazy(() => import("../pages/Lead/assignment-sales"))
const leadAssignmentSalesEditor = lazy(() => import("../pages/Lead/assignment-sales-editor"))
const leadAssignmentSalesPublish = lazy(() => import("../pages/Lead/assignment-sales-publish"))
const leadHomeValuationEditor = lazy(() => import("../pages/Lead/home-valuation-editor"))
const leadHomeValuationPublish = lazy(() => import("../pages/Lead/home-valuation-publish"))
const campMailEditor = lazy(() => import("../pages/Campaign/camp-mail-editor"))
const campMailEditors = lazy(() => import("../pages/Campaign/camp-mail-editors"))
const sendCampMail = lazy(() => import("../pages/Campaign/send-camp-mail"))
const campNewMailEditors = lazy(() => import("../pages/Campaign/camp-new-mail-editors"))
const campMailTempNew = lazy(() => import("../pages/Campaign/camp-mail-editor-new"))
const emailTemplates = lazy(() => import("../pages/Settings/email-templates"))
const emailConnect = lazy(() => import("../pages/Settings/general/email-connect"))
const clientInbox = lazy(() => import("../pages/Clients/client-inbox"))
const ContactReferral = lazy(() => import("../pages/Contacts/questions/refferral"))
const customerSupport = lazy(() => import("../pages/Settings/customer-support"))
const helpfulDocuments = lazy(() => import("../pages/Settings/helpful-documents"))
const helpfulLinks = lazy(() => import("../pages/Settings/helpful-links"))
const allNotification = lazy(() => import("../pages/Dashboard/all-notification"))
const CustomeTemplates = lazy(() => import("../pages/Settings/email-templates/custom-template"))
const UpdateCustomTemplate = lazy(() => import("../pages/Pre-Construction/Projects/email-template-update"))
const ClientDealCondition = lazy(() => import("../pages/Settings/ClientDealCondition"))
const DealChecklist = lazy(() => import("../pages/Settings/DealChecklist"))
const clientActive = lazy(() => import("../pages/Clients/client-active"))
const offerCompare = lazy(() => import("../pages/Clients/offer-compare"))
const Error400 = lazy(() => import("../pages/Errors/error-400"))
const Error403 = lazy(() => import("../pages/Errors/error-403"))
const Error500 = lazy(() => import("../pages/Errors/error-500"))
const EmailSeller = lazy(() => import("../pages/Email/email-seller"))
const EmailInquiry = lazy(() => import("../pages/Email/email-inquiry"))
const customeEmailTeamplate = lazy(() => import("../pages/Editor/custome-email-teamplate"))
const manageRole = lazy(() => import("../pages/Settings/manage-role"))
const upcomingClosing = lazy(() => import("../pages/Clients/upcoming-closing"))
const supportTicket = lazy(() => import("../pages/Settings/support-ticket"))
const ApiPage = lazy(() => import("../pages/Settings/general/apis"))
const EntityAuditLogPage = lazy(() => import("../pages/Settings/general/entity-audit-log"))
const WebflowEmailTemplates = lazy(() => import("../pages/Settings/email-templates/webflow-emails"))
const PublicVideoPlayer = lazy(() => import("../pages/Public/video-player"))

const authProtectedRoutes = [
  { path: "/dashboard", component: DashboardNew },
  { path: "/test", component: Test },
  { path: "/cal", component: Cal },
  { path: "/pre-construction/create-project/description/:projectId", component: ProjectUpdate },
  { path: "/pre-Construction/create-project/feature/:projectId", component: ProjectsFeature },
  { path: "/pre-Construction/create-project/contact/:projectId", component: ProjectsContact },
  { path: "/pre-Construction/create-project/media/:projectId", component: ProjectsMedia },
  { path: "/pre-Construction/create-project/link/:projectId", component: ProjectLink },
  { path: "/pre-Construction/create-project/gallery/:projectId", component: ProjectsGallery },
  { path: "/pre-Construction/create-project/marketing/:projectId", component: ProjectsMarketing },
  { path: "/pre-construction/campaign/marketing/social-media/:projectId", component: projectSocialMedia },
  { path: "/pre-construction/campaign/marketing/:projectId", component: projectMarket },
  { path: "/pre-construction/campaign/budget/:projectId", component: projectBudget },
  { path: "/pre-construction/campaign/srr-filled/:projectId", component: projectSrr },
 
  { path: "/pre-construction/campaign/rsvp/:projectId", component: projectRsvp },
  { path: "/pre-construction/campaign/current-prospects/:projectId", component: projectCurrent },
  { path: "/pre-construction/campaign/client/:projectId", component: projectClient },
  { path: "/pre-construction/campaign/download-price/:projectId", component: projectDownloadPrice },
  { path: "/pre-construction/campaign/integration/:id/:projectId", component: ProjectIntegration },
  { path: "/pre-construction/campaign/send-email/:projectId", component: projectMail },
  { path: "/pre-construction/campaign/landing-page/:projectId", component: ProjectLandingPage },
  { path: "/pre-construction/campaign/worksheet/:projectId", component: projectWorksheet },
  { path: "/pre-construction/campaign/open-house-page/:projectId", component: ProjectOpenHousePage },
   { path: "/pre-construction/campaign/inquiry/:projectId", component: ProjectWorksheetInquiry },
  { path: "/pre-construction/campaign/download-price-page/:projectId", component: ProjectDownloadPricePage },
  { path: "/pre-construction/campaign/setting/:id/:projectId", component: projectsSetting },
  { path: "/Pre-Construction/campaign/:projectId", component: Campaign },
   
 
  { path: "/pre-construction/mail-submit/:id/:projectId", component: projectMailSubmit },
  { path: "/pre-construction/new-mail-editor/:projectId/", component: projectMailNew },
  { path: "/pre-construction/mail-editor/:projectId/:templateId", component: projectMailEditor },
    { path: "/pre-construction/mail-editors/:projectId/:templateId", component: projectDraftMailEditor },
  { path: "/pre-construction/mail-editor-new/:projectId/:templateId", component: projectMailEditorNew },

    { path: "/pre-construction/email-update/:templateId", component: UpdateCustomTemplate },
  
  { path: "/pre-construction/invite-editor/:id/:invId/:templateId", component: projectInviteEditor },
  { path: "/pre-construction/landing-editor/:projectId/:templateId", component: editorLandingpage },
  { path: "/pre-construction/publish-page/:id/:projectId/:templateId", component: PublishPage },

  //{ path: "/pre-Construction/create-project/payment", component: ProjectsPayment },
  { path: "/pre-construction/mail-send/:projectId/:campId", component: Sendcamp },
  { path: "/pre-construction/invite-send/:id/:invId/:campId", component: SendInvite },
  { path: "/pre-construction/builder-detail/:id/:builderId", component: BuilderDetail },
  { path: "/pre-construction/bproject-detail/:id/:projectId", component: ProjectBuilderDetail },


  { path: "/pre-construction/builder/create", component: BuilderCreate },

  { path: "/pre-construction/my-project", component: Project },
  { path: "/pre-construction/compare-project", component: ProjectCompare },
  { path: "/pre-construction/create-new-project", component: ProjectsCreate },
  { path: "/pre-construction/project-detail/:id", component: ProjectDetail },
  { path: "/pre-construction/campaign/:projectId", component: Campaign },
  { path: "/pre-construction/srr-create", component: srrCreate },
  { path: "/Pre-Construction/my-project", component: Project },
  { path: "/Pre-Construction/compare-project", component: ProjectCompare },
  { path: "/Pre-Construction/create-new-project", component: ProjectsCreate },

  //{ path: "/pre-construction/builder/builder-detail", component: BuilderDetail },

  //{ path: "/Pre-Construction/project-detail", component: ProjectDetail },
  { path: "/Pre-Construction/project-detail/:id", component: ProjectDetail },

  { path: "/pre-construction/srr-print/:id", component: projectSrrPrint },
  { path: "/pre-construction/campaign/send-email", component: projectMail },
  

  

 
  { path: "/Pre-Construction/srr-create", component: srrCreate },

  { path: "/landing-page/create-new", component: landingPageCreateNew },
  { path: "/landing-page", exact: true, component: landingPage },
  //{ path: "/create-theme", component: createTemplate },
  { path: "/pre-construction/builder", component: Builder },
  { path: "/pre-construction/campaign/mass-email", component: massMailDashboard },
  { path: "/pre-construction/email/create-new", component: projectSelectTemp },
  { path: "/pre-construction/email", component: emaillandingPage },
 
  
  { path: "/pre-construction/landing-editor", component: projectLandingEditor },
  { path: "/pre-construction/landing-page-submit", component: projectLandingSubmit },


  //Project Lead
  
  {path:"/pre-construction/project/lead/worksheet-pending", component:PendingWorksheetLead },
  {path:"/pre-construction/project/lead/worksheet-submitted", component:SubmittedWorksheetLead },
  {path:"/pre-construction/project/lead", component:ProjectLead },
  
  {path:"/pre-construction/project/clients", component:ProjectClients },



  //Update Project


  { path: "/pre-Construction/edit-project/feature/:id/:projectId", component: ProjectEditFeature },
  { path: "/pre-Construction/edit-project/contact/:id/:projectId", component: ProjectEditContact },
  { path: "/pre-Construction/edit-project/media/:id/:projectId", component: ProjectEditMedia },
  { path: "/pre-Construction/edit-project/gallery/:id/:projectId", component: ProjectsEditGallery },
  { path: "/pre-Construction/edit-project/marketing/:id/:projectId", component: ProjectEditMarketing },
  { path: "/pre-Construction/edit-project/:id/:projectId", component: ProjectsEdit },

  { path: "/lead/lead-detail/:id/:leadId", component: LeadDetail },
  { path: "/lead/buyer-preference/:id/:leadId", component: BuyerQuestionAnswer },
  { path: "/lead/buyer-eligibility/:id/:leadId", component: BuyerEligibility },
  {path: "/lead/selling-preferences/:id/:leadId", component: SellerQuestionAnswer },
  {path: "/lead/investment-preferences/:id/:leadId", component: InvestorQuestionAnswer },
  
  { path: "/lead/report/pipeline-status", component: leadPipeStatus },
  { path: "/lead/report/activity-status", component: LeadEngageMentView },
  { path: "/lead/eligibility/question", component: LeadEligibility },
    { path: "/lead/eligibility/overview", component: eligibilityOverview },
  { path: "/lead/lead-generation", component: leadGeneration },
  { path: "/lead/events", component: leadEvents },
  { path: "/lead/referral-offers/editor", component: leadReferralsLandingEditor },
  { path: "/lead/referral-offers", component: leadReferralsLanding },
  { path: "/lead/referrals", exact: true, component: leadReferrals },
  { path: "/lead/home-valuation/editor", component: leadHomeValuationEditor },
  { path: "/lead/home-valuation/publish", component: leadHomeValuationPublish },
  { path: "/lead/home-valuation", component: leadHomeValuation },
  { path: "/lead/assignment-sales/editor/:id", component: leadAssignmentSalesEditor },
  { path: "/lead/assignment-sales/publish/:id", component: leadAssignmentSalesPublish },
  { path: "/lead/assignment-sales", component: leadAssignmentSales },
  { path: "/lead/eligibility/decision", component: DecisionEligibility },
  { path: "/lead/eligibility/mortgage", component: MortgageEligibility },
  { path: "/lead/eligibility/reason", component: ReasonEligibility },

  
  { path: "/lead/dashboard", component: Dashboard },
  { path: "/lead/archived", component: Dashboard },
  { path: "/lead/pool", component: Dashboard },
  { path: "/lead/insight", component: LeadInsight },
  { path: "/lead/lead-create", component: CreateLead },
  { path: "/lead/leadlist", component: LeadList },
  { path: "/lead/stage-view", component: LeadStagePage },
  { path: "/lead/timeline-view", component: leadTimeline },
  { path: "/lead/status-view", component: LeadStatusReport },
  { path: "/lead/type-view", component: LeadTypeReport },
  { path: "/lead/group-view", component: LeadGroupReport },
  
  { path: "/lead/acquire-lead", component: AcquireLead },
  { path: "/lead/import-lead", component: importLead },
  { path: "/lead/map-import-lead", component: mapImportLead },
  { path: "/lead/import-lead-submitted", component: FinalImportLead },
  { path: "/lead/web-form", component: webLeadForm },
  { path: "/lead/new-web-form", component: newWebLeadForm },

  { path: "/lead/form-design", component: newWebLeadFormDesign },



  //Activity

  { path: "/activity", component: Activity },
  { path: "/appointment", component: Appointment },
  { path: "/notes", component: Notes },

  { path: "/task/create", component: TasksCreate },
  { path: "/task", component: ourTask },

  { path: "/action-plan/create", component: ActionPlanCreate },
  { path: "/action-plan/edit/:id", component: ActionPlanCreate },
  {
    path: "/action-plan/folder/:id",
    component: RedirectActionPlanFolder,
  },
  { path: "/action-plan", exact: true, component: RedirectActionPlans },
  { path: "/setting/action-plans/folder/:id", component: ActionPlanByFolder },
  { path: "/setting/action-plans", component: ActionPlan },


  //Contact 

  { path: "/Contacts", component: Contacts },

  { path: "/all-contacts", component: AllContacts },
  { path: "/contact/contact-detail/:id/:contactid", component: ContactDetail },
  { path: "/contact/buyer-preference/:id/:contactid", component: ContactBuyerQuestionAnswer },
  { path: "/contact/additional-profile/:id/:contactid", component: ContactAdditionProfile },
  { path: "/contact/referrals/:id/:contactid", component: ContactReferral },
  { path: "/contact/selling-preferences/:id/:contactid", component: ContactSellerQuestionAnswer },
  { path: "/contact/investment-preferences/:id/:contactid", component: ConatctInvestorQuestionAnswer },
  
  { path: "/contact/import-contact", component: ImportContact },
  { path: "/contact/map-import-contact", component: MapImportContact },
  { path: "/contact/import-contact-submitted", component: FinalImportContact },
  { path: "/contact/contact-report", component: ContactReport },
  { path: "/contact/contact-create", component: ContactCreate },
  { path: "/contact/contact-edit", component: EditContact },
  { path: "/contact/classification-view", component: ClassificationContact },
  { path: "/contact/activity", component: ClientActivity },
  
  { path: "/contact/referals", component: ContactAllReferral },
  { path: "/contact/review", component: ContactReview },
  { path: "/contact/review-history", component: ReviewHistory },
  { path: "/contact/email-history", component: EmailHistory },

  // Email Composer

  { path: "/email", component: EmailComposer },
  { path: "/email-2", component: EmailSec },
  { path: "/new-email", component: NewEmail },
  

  //CLients

  
  { path: "/clients/dashbord/:id/:clientId", component: ClientDashbord },
  { path: "/clients/profile/primary/:id/:clientId", component: ClientProfile },
  { path: "/clients/profile/secondary/:id/:clientId", component: clientSecProfile },
  { path: "/clients/profile/important-date/:id/:clientId", component: clientImpProfile },
  { path: "/clients/profile/faimily-info/:id/:clientId", component: clientFaimilyProfile },

  { path: "/clients/activity/engagement/:id/:clientId", component: ClientEngagement },
  { path: "/clients/activity/deal/:id/:clientId", component: ClientDeal },
  { path: "/clients/activity/appointment/:id/:clientId", component: ClientApp },
   { path: "/clients/activity/inbox/:id/:clientId", component: clientInbox },
  { path: "/clients/activity/documents/:id/:clientId", component: ClientDocument },
  { path: "/clients/activity/message/:id/:clientId", component: ClientMessage },
  { path: "/clients/activity/note/:id/:clientId", component: ClientNote },
  { path: "/clients/activity/action-plans/:id/:clientId", component: ClientActionPlans },
  
  
  
  
  { path: "/clients/seller/property/:id/:clientId", component: ClientSale },
  { path: "/clients/seller/property-page/:id/:clientId/:listingId", component: ClientSellerPropertyPage },
  { path: "/clients/seller/leads/:id/:clientId", component: ClientSellerLeads },
  { path: "/clients/seller/open-house/:id/:clientId", component: ClientSellerOpen },
  { path: "/clients/seller/questions/:id/:clientId", component: ClientSalePref },
  { path: "/clients/seller/showings/:id/:clientId", component: ClientSellerShow },
    { path: "/clients/seller/compare/:id/:clientId", component: offerCompare },
  { path: "/clients/seller/checklists/:id/:clientId", component: ClientSellerCheck },
  { path: "/clients/seller-closing/:id/:clientId", component: ClientSellerClose },
  
  
  { path: "/clients/seller-market/:id/:clientId", component: ClientSellerMarket },
  { path: "/clients/open-list/:id/:clientId/:openhouseId", component: OpenList },
  { path: "/clients/lead-detail/:id/:clientId", component: ClientLeadDetail },


  { path: "/clients/buyer/listings/:id/:clientId", component: clientListing },
  { path: "/clients/buyer/questions/:id/:clientId", component: ClientBuyPref },
  { path: "/clients/buyer/preferences/:id/:clientId", component: ClientHomePref },
  { path: "/clients/buyer/checklists/:id/:clientId", component: ClientBuyerCheck },

  { path: "/clients/buyer-closing/:id/:clientId", component: ClientBuyerClose },
  { path: "/clients/buyer-market/:id/:clientId", component: ClientBuyerMarket },
 

  
  { path: "/clients/investor/investments/:id/:clientId", component: ClientInvProj },
  { path: "/clients/profile/active/:id/:clientId", component: clientActive },
  { path: "/clients/investor/questions/:id/:clientId", component: ClientInvPref },
  { path: "/clients/import-client", component: ImportClient },
  { path: "/clients/map-import-client", component: MapImportClient },
  { path: "/clients/import-client-submitted", component: FinalImportClient },
  { path: "/clients", component: Clients },
  { path: "/in-clients", component: InClients },
  { path: "/rental-clients", component: rentalTimeline },
  { path: "/upcoming-closing", component: upcomingClosing },
  { path: "/create-client", component: ClientCreate },
  { path: "/message", component: ClientMessage },
  { path: "/resource", component: OurResource },

  { path: "/office/message", component: Messages },
  { path: "/office/resource", component: OurResource },
  { path: "/office/events", component: Events },

  // Agent Setting Page
  { path: "/setting/object/manage-marketing", component: manageMarketing },

  { path: "/setting/object/app-location-type", component: AppointmentLocationTypes },
  { path: "/setting/object/app-priority", component: AppointmentPriority },
  { path: "/setting/object/app-type", component: AppointmentType },
  { path: "/setting/object/contact-level", component: ContactLevel },
  { path: "/setting/object/contact-status", component: ContactStatus },
  { path: "/setting/object/contact-type", component: ContactType },
  { path: "/setting/object/lead-classification", component: LeadClassifications },
  { path: "/setting/object/lead-source", component: LeadSource },
  { path: "/setting/object/lead-status", component: LeadStatus },
  { path: "/setting/object/lead-type", component: LeadType },
  { path: "/setting/object/lead-stage", component: LeadStage },
  { path: "/setting/object/note-type", component: NoteType },
  { path: "/setting/object/project-doc-type", component: ProjectDocumentType },
  { path: "/setting/object/project-status", component: ProjectStatus },
  { path: "/setting/object/project-type", component: ProjectType },
  { path: "/setting/object/service-type", component: ServiceType },
  { path: "/setting/object/task-priority", component: TaskPriority },
  { path: "/setting/object/task-reminder", component: TaskReminder },
  { path: "/setting/object/task-status", component: TaskStatus },
  { path: "/setting/object/task-type", component: TaskType },
  { path: "/setting/object/transaction-type", component: TransactionType },
  { path: "/setting/object/transaction-comm-type", component: TransactionCommType },
  { path: "/setting/object/transaction-ref-type", component: TransactionRefType },
  { path: "/setting/object/transaction-status-type", component: TransactionStatusType },
  { path: "/setting/object/listing-type", component: ListingType },
  { path: "/setting/object/listing-status", component: ListingStatus },

  { path: "/setting/object/email-list-type", component: EmailListType },
  { path: "/setting/object/email-list-target", component: EmailListTarget },
  { path: "/setting/object/campaign-email-list", component: CampaignEmailList },
  { path: "/setting/object/offer-condition", component: ClientDealCondition },
    { path: "/setting/object/deal-checklist", component: DealChecklist },

  { path: "/setting/agent-profile", component: agentProfile },
  { path: "/setting/general/currency", component: GenralCurrency },
   {path:"/setting/general/connect-email", component:emailConnect},
  { path: "/setting/general/security", component: GenralSecurity },
  { path: "/setting/general/calendar", component: GenralCalendar },
  { path: "/setting/general/emailmapper", component: EmailMapper },
  { path: "/setting/general/apis", component: ApiPage },
  { path: "/setting/general/connected-apps", component: ConnectedAppsPage },
  { path: "/setting/general/entity-audit-log", component: EntityAuditLogPage },


{ path: "/setting/email-templates/all", component: emailTemplates },
{ path: "/setting/webflow-emails/action-plan-emails", component: WebflowEmailTemplates },
  { path: "/setting/email-templates/signature", component: EmailSignature },
  { path: "/setting/email-templates/social-media", component: SocialMedia },
    { path: "/setting/general/review-distribution", component: reviewDistribution },
  
  { path: "/setting/membership", component: memberInfo },
  { path: "/setting/communication/messaging-setup", component: CommunicationMessagingSetup },
  { path: "/setting/communication", component: CommunicationPage },
  { path: "/setting/support", component: techSupport },
  { path: "/setting/customer-support", component: customerSupport },
    { path: "/setting/support-ticket", component: supportTicket },
    { path: "/setting/custom-email-template", component: CustomeTemplates },
  { path: "/setting/helpful-documents", component: helpfulDocuments },
  { path: "/setting/helpful-links", component: helpfulLinks },
  { path: "/setting/object", component: settingObject },
  { path: "/setting/users", component: Users },
  { path: "/setting/manage-role", component: manageRole },
  { path: "/setting/add-user", component: AddUser },
  { path: "/setting/permission", component: UserPermission },


  //All Templates

  { path: "/all-templates", component: ourTemplates },
  { path: "/templates-detail/:campType", component: ReferalTemp },
  { path: "/review-templates", component: ReviewTemp },
  //{ path: "/create/:type/:page", component: createNewTemplate },
  //{ path: "/create-landingpage/:type/:page", component: createNewLandinTemplate },
  { path: "/form-design/:type/:page", component: formDesign },

  { path: "/calendar", component: Calendar },
  { path: "/social/get-started", component: SocialPage },
  { path: "/social/posts", component: SocialPage },
  { path: "/social/opportunities", component: SocialPage },
  { path: "/social/create-post", component: SocialPage },
  { path: "/social/calendar", component: SocialPage },
  { path: "/social/inbox", component: SocialPage },
  { path: "/social/promote", component: SocialPage },
  { path: "/social/analytics", component: SocialPage },
  { path: "/social/connections", component: SocialPage },
  { path: "/social", component: SocialPage },
 { path: "/campaign/mail-send/:id/:campId", component: sendCampMail },
  // Campaigns hub pages - must stay above the non-exact "/campaign" route below
  { path: "/campaign/:hubKey(home-valuation|events|referrals|assignment-sale|newsletter|greetings)", component: campaignEmailPage },
  { path: "/campaign", component: CreateComp },
  { path: "/reviews", component: ReviewCamp },
  { path: "/surveys", component: SurveyCamp },
  { path:"/referral", component:ReffralCamp},
  { path:"/newsletter", component:NewsletterCamp},
  { path:"/seller", component:SellerCamp},

  { path: "/create-campaign/:campType", component: CreateCompTemp },

  { path: "/create-new-campaign/:campType/:name", component: CreateCompAutoTemp },
  { path: "/create-campaign", component: CreateCompTemp },
  
  { path: "/choose-contact/:templateId", component: chooseTemplateComp },
  { path: "/campaign-detail/:campType", component: NewsletterCamp },
    {path: "/campaign-new-mail/:campType", component:campNewMailEditors},
  {path: "/campaign-mail/:templateId", component:campMailEditor},
  {path: "/campaign-mails/:templateId/:campId", component:campMailEditors},
  { path: "/camp-email-editor/:templateId/:campType", component: campMailTempNew },

  //{path: "/editor/:templateId/:campId/:campuuid", component: compEditor },
  //{ path: "/editor/:templateId", component: compEditor },
  { path: "/form-editor", component: formDesignEdit },


  { path: "/collection/list", component: CollectionList },
  { path: "/collection", component: CollectionDashboard },


  {path:"/transaction/deal", component:DealDashboard },
  { path: "/transaction", component: TransactionDashboard },

  { path: "/goal", component: ReportGoals },


  { path: "/master-landing-page/create", component: MasterLandingCreate },
  { path: "/master-landing-page/edit/:id", component: MasterLandingEdit },
  { path: "/master-landing-page", exact: true, component: MasterLandingPageList },

  { path: "/custom-email-template", component: CustomTemplates },
  { path: "/email-master", component: MasterEmail },
  {path:"/custom-template", component:customeEmailTeamplate},



  { path: "/test-upload", component: UploadFile },

  { path: "/editor-email/:campType", component: UnlayerEditor },



 { path: "/preview-landing-page", component: PreviewLandingPage },
 
 { path: "/email-inbox", component: EmailInbox },
 { path: "/email-sent", component: EmailSent },
 { path: "/email-read/:id", component: EmailRead },
 { path: "/email-assign/:id", component: EmailAssign },
{ path: "/email-seller/:id", component: EmailSeller },
 { path: "/email-inquery/:id", component: EmailInquiry },
 { path: "/calendar", component: Calendar },
 {path:"/notification", component:allNotification},
  // this route should be at the end of all other routes
  // eslint-disable-next-line react/display-name
  { path: "/", exact: true, component: () => <Redirect to="/dashboard" /> }
]

const publicRoutes = [

  { path: "/logout", component: Logout },
  { path: "/login", component: Login },
   { path: "/resetpassword", component: ResetPasswords },
   // Alias for consistency (some users type with dash)
  { path: "/forgot-password", component: ForgetPassword },
  { path: "/event-invite/:projectEventInviteId", component: ProjectInviteUser },
  { path: "/theme-one/:id/:projectId", component: ThemeOne },
  { path: "/realtor-openhouse-add-lead/:clientOpenhouseLeadSourceId", component: ClientLeadForm },
  { path: "/lead/referrals", exact: true, component: leadReferrals },
  { path: "/contact/referals-send", component: ReferalSend },
  { path: "/contact/referals-template", component: ReferalTemplate },
  //{ path: "/contact/referals-template-create", component: ReferalTemplateCreate },
  { path: "/contact/review-send", component: ReviewSend },
  { path: "/contact/review-template", component: ReviewTemplate },
  //{ path: "/contact/review-template-create", component: ReviewTemplateCreate },
  { path: "/upload-worksheet", component: WorkSheetFront },
  { path: "/pre-construction/campaign/open-house-form/:id/:visitorId", component: ProjectOpenHouseForm },
  { path: "/pre-construction/campaign/download-price-form/:id/:visitorId", component: ProjectDownloadPriceForm },
  { path: "/pre-construction/campaign/worksheet-form/:leadId/:id/:visitorId", component: ProjectWorksheetForm },
  { path: "/pre-construction/campaign/worksheet-thank/:leadId/:id/:visitorId", component: ProjectWorksheetThank },
  { path: "/pre-construction/preview-page/:id/:projectId/:templateId", component: previewPage },
  { path: "/pre-construction/new-preview-page/:id/:projectId/:templateId", component: PreviewNewPage },
  { path: "/gmail", component: GmailLanding },
  { path: "/google", component: GoogleLanding },
  { path: "/play-video", component: PublicVideoPlayer },
  { path: "/error-400", component: Error400 },
  { path: "/error-403", component: Error403 },
  { path: "/error-500", component: Error500 },
  
  
  

]

export { authProtectedRoutes, publicRoutes }
